/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, NumberFormatter, NumberInput, Skeleton } from '@mantine/core';
import { Trash } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import roundHalfEven from 'round-half-even';
import AppCard from '../../../../common/AppCard';
import AppFlexbox from '../../../../common/AppFlexbox';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import AppImage from '../../../../common/AppImage';
import ProductLineItemAddonsDisplay from '../../../products/ProductLineItemAddonsDisplay';
import OrderProductsTaxInfo from '../../../orders/OrderProductsTaxInfo';
import WsCheckoutCartProductCardAlert from './WsCheckoutCartProductCardAlert';
import WsCheckoutCartProductCardBundleItems from './WsCheckoutCartProductCardBundleItems';
import { getBundleTaxItems } from '../../../../../helpers/storeHelper';

const WsCheckoutCartProductCard = ({
  lineItem,
  lineItemInfo,
  isOrderHistory,
  mediaQueries,
  loading,
  onRemove,
  submitting,
  productUrl,
  onNavigate,
  onUpdate,
  onCustomize,
  onRemoveUnavailableProducts
}) => {
  const [debouncedQuantity, setDebouncedQuantity] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const { isTabletOrSmaller } = mediaQueries;

  const {
    previewImage,
    quantityLeft,
    outOfStock,
    tooManyInCart,
    isUnavailable,
    hasChanges,
    requiresProductUpdate,
    requiresPrintImagesUpdate,
    latestVersionEcomStoreCartProduct,
    packageItemsWithReferences,
    ecomStoreCartProduct,
    ecomStoreCartPackage
  } = lineItemInfo || {};

  useEffect(() => {
    if (lineItem) {
      setQuantity(lineItem.quantity);
    }
  }, [lineItem]);

  useEffect(() => {
    if (debouncedQuantity !== null) {
      if (debouncedQuantity !== lineItem.quantity) {
        onUpdate(
          lineItem.isBundleProduct
            ? {
                ecomStoreCartPackages: [
                  {
                    pkEcomStoreCartPackage: lineItem.entityId,
                    count: debouncedQuantity
                  }
                ]
              }
            : {
                ecomStoreCartProducts: [
                  {
                    pkEcomStoreCartProduct: lineItem.entityId,
                    uuid:
                      ecomStoreCartProduct.ecomStoreProductVariant
                        .ecomVendorProductVariant.uuid,
                    count: debouncedQuantity
                  }
                ]
              }
        );
      }
    }
  }, [debouncedQuantity]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuantity(quantity);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [quantity]);

  return lineItem ? (
    <AppCard
      shadow="xs"
      style={{ padding: 0, borderRadius: isTabletOrSmaller ? 0 : 8 }}
      withBorder
    >
      <WsCheckoutCartProductCardAlert
        hasChanges={hasChanges}
        isBundleProduct={!!ecomStoreCartPackage}
        isOrderHistory={isOrderHistory}
        isUnavailable={isUnavailable}
        latestVersionEcomStoreCartProduct={latestVersionEcomStoreCartProduct}
        onCustomizeProduct={() => onCustomize(ecomStoreCartProduct)}
        onRemoveUnavailableProducts={onRemoveUnavailableProducts}
        outOfStock={outOfStock}
        quantityLeft={quantityLeft}
        requiresPrintImagesUpdate={requiresPrintImagesUpdate}
        requiresProductUpdate={requiresProductUpdate}
        submitting={submitting}
        tooManyInCart={tooManyInCart}
      />
      <AppStack style={{ gap: 0, flexWrap: 'nowrap' }}>
        <AppFlexbox
          style={{
            flex: 1,
            margin: 16,
            flexWrap: 'nowrap',
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <AppFlexbox
            style={{ flexWrap: 'nowrap', alignSelf: 'stretch', flex: 2 }}
          >
            <AppFlexbox style={{ alignSelf: 'start', flex: 1 }}>
              <AppFlexbox component={Link} onClick={onNavigate} to={productUrl}>
                <AppImage
                  fit="contain"
                  height={isTabletOrSmaller ? 70 : 100}
                  src={previewImage}
                  width={isTabletOrSmaller ? 70 : 100}
                />
              </AppFlexbox>

              <AppStack
                style={{
                  flex: 1,
                  gap: 20,
                  justifyContent: 'center'
                }}
              >
                <AppStack
                  style={{
                    flex: 1,
                    gap: 0
                  }}
                >
                  <AppStack
                    component={Link}
                    onClick={onNavigate}
                    style={{
                      gap: 0,
                      textDecoration: 'none',
                      color: '#000'
                    }}
                    to={productUrl}
                  >
                    <AppText style={{ fontSize: 18, fontWeight: 700 }}>
                      {lineItem.productName}
                    </AppText>

                    {!lineItem.isBundleProduct && (
                      <ProductLineItemAddonsDisplay
                        addOns={lineItem.addOns}
                        showVariantOptionsOnSingleLine
                      />
                    )}

                    <AppStack style={{ gap: 12, marginTop: 12 }}>
                      <WsCheckoutCartProductCardBundleItems
                        imageSize={isTabletOrSmaller ? 40 : 60}
                        packageItemsWithReferences={packageItemsWithReferences}
                      />
                    </AppStack>

                    {isOrderHistory && (
                      <AppText style={{ fontSize: 12, color: '#666' }}>
                        Qty: {lineItem.quantity}
                      </AppText>
                    )}
                  </AppStack>
                </AppStack>
                {!isOrderHistory && (
                  <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                    <NumberInput
                      allowNegative={false}
                      disabled={
                        outOfStock || submitting || isUnavailable || hasChanges
                      }
                      min={1}
                      onChange={(v) => {
                        let newQuantity =
                          quantityLeft !== null && v > quantityLeft
                            ? quantityLeft
                            : v;
                        if (newQuantity <= 0) {
                          newQuantity = 1;
                        }
                        setQuantity(newQuantity);
                      }}
                      size="xs"
                      style={{ flex: 1, maxWidth: 80 }}
                      value={quantity}
                    />
                    {!submitting &&
                      !isUnavailable &&
                      !hasChanges &&
                      lineItem.isCustomizable && (
                        <Anchor
                          onClick={() => onCustomize(ecomStoreCartProduct)}
                          style={{ fontSize: 14, color: 'dodgerblue' }}
                          underline="always"
                        >
                          Customize
                        </Anchor>
                      )}
                  </AppFlexbox>
                )}
              </AppStack>
            </AppFlexbox>
          </AppFlexbox>

          <AppStack
            style={{
              alignSelf: 'start',
              placeItems: 'end',
              marginLeft: 'auto',
              gap: 10
            }}
          >
            <AppStack style={{ gap: 0, alignItems: 'end' }}>
              <AppText style={{ fontSize: 18 }} weight={700}>
                <NumberFormatter
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  suffix={` ${lineItem.currency?.toUpperCase()}`}
                  thousandSeparator
                  value={(lineItem.subtotalInCents * lineItem.quantity) / 100}
                />
              </AppText>

              <AppText style={{ fontSize: 14, color: '#666' }}>
                <NumberFormatter
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  suffix={` each`}
                  thousandSeparator
                  value={lineItem.subtotalInCents / 100}
                />
              </AppText>

              {lineItem.tax.length > 0 && (
                <OrderProductsTaxInfo
                  taxItems={[
                    {
                      entityId: lineItem.entityId,
                      tax: lineItem.tax.map((t) => ({
                        name: t.name,
                        taxRate: t.taxRate,
                        taxAmount:
                          roundHalfEven(
                            (lineItem.subtotalInCents * t.taxRate) / 100,
                            0
                          ) * lineItem.quantity
                      }))
                    }
                  ]}
                />
              )}

              {lineItem.bundleItems.some((bi) => bi.tax.length > 0) && (
                <OrderProductsTaxInfo taxItems={getBundleTaxItems(lineItem)} />
              )}

              {lineItem.customerPaysServiceFees &&
                lineItem.serviceFeeInCents > 0 && (
                  <AppFlexbox style={{ gap: 5, flexWrap: 'nowrap' }}>
                    <AppText weight={500}>
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="+ $"
                        suffix=" Service fee"
                        thousandSeparator
                        value={lineItem.serviceFeeInCents / 100}
                      />
                    </AppText>
                  </AppFlexbox>
                )}
            </AppStack>
            {!isOrderHistory && (
              <AppStack style={{ gap: 20, flex: 1 }}>
                <AppFlexbox
                  onClick={() => {
                    if (!submitting) {
                      onRemove(
                        lineItem.isBundleProduct
                          ? { pkEcomStoreCartPackages: [lineItem.entityId] }
                          : { pkEcomStoreCartProducts: [lineItem.entityId] }
                      );
                    }
                  }}
                  style={{
                    cursor: submitting ? 'default' : 'pointer',
                    gap: 5,
                    alignSelf: 'end',
                    alignItems: 'center',
                    color: submitting ? '#999' : '#c40000'
                  }}
                >
                  <Trash size={18} />
                  <AppText style={{ fontSize: 14 }}>Remove</AppText>
                </AppFlexbox>
              </AppStack>
            )}
          </AppStack>
        </AppFlexbox>
      </AppStack>
    </AppCard>
  ) : (
    <AppCard
      radius={isTabletOrSmaller ? 0 : 'md'}
      shadow="xs"
      style={{ padding: 0, borderRadius: 8 }}
      withBorder
    >
      <AppStack style={{ gap: 0, flexWrap: 'nowrap' }}>
        <AppFlexbox
          style={{
            flex: 1,
            margin: 30,
            flexWrap: 'nowrap'
          }}
        >
          <AppFlexbox
            style={{ flexWrap: 'nowrap', alignSelf: 'stretch', flex: 2 }}
          >
            <AppFlexbox style={{ alignSelf: 'start', flex: 1 }}>
              <Skeleton
                height={isTabletOrSmaller ? 70 : 100}
                width={isTabletOrSmaller ? 70 : 100}
              />
              <AppStack
                style={{
                  flex: 1,
                  gap: 10,
                  textDecoration: 'none',
                  justifyContent: 'center',
                  color: '#000'
                }}
              >
                <Skeleton height={16} width="50%" />
                <Skeleton height={14} width="40%" />
              </AppStack>
            </AppFlexbox>
          </AppFlexbox>

          <AppStack
            style={{
              flex: 1,
              alignSelf: 'start',
              placeItems: 'end',
              marginLeft: 'auto',
              gap: 10
            }}
          >
            <AppStack style={{ gap: 0, alignItems: 'end' }}>
              <Skeleton height={20} width={100} />
            </AppStack>

            {!isOrderHistory && (
              <AppFlexbox style={{ cursor: 'pointer', gap: 10 }}>
                <Skeleton height={15} width={80} />
              </AppFlexbox>
            )}
          </AppStack>
        </AppFlexbox>
      </AppStack>
    </AppCard>
  );
};

WsCheckoutCartProductCard.propTypes = {
  isOrderHistory: PropTypes.bool,
  lineItem: PropTypes.object,
  lineItemInfo: PropTypes.object,
  loading: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onCustomize: PropTypes.func,
  onNavigate: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveUnavailableProducts: PropTypes.func,
  onUpdate: PropTypes.func,
  productUrl: PropTypes.string,
  submitting: PropTypes.bool
};

export default WsCheckoutCartProductCard;
